import { createContext, useCallback, useContext, useEffect, useRef, useState, type ButtonHTMLAttributes, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { Icon, type IconName } from './icons';
import { formatCents, parseMoney } from '../../shared/money';
import { ApiError, get } from '../lib/api';

// ---------- buttons ----------

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  icon?: IconName;
  block?: boolean;
  busy?: boolean;
};

export function Button({ variant = 'secondary', size = 'md', icon, block, busy, children, className = '', disabled, ...rest }: BtnProps) {
  const C = icon ? Icon[icon] : null;
  return (
    <button
      type="button"
      className={`btn btn-${variant} ${size !== 'md' ? `btn-${size}` : ''} ${block ? 'btn-block' : ''} ${className}`}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {C ? <C /> : null}
      {busy ? 'Working…' : children}
    </button>
  );
}

export function IconButton({ icon, label, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { icon: IconName; label: string }) {
  const C = Icon[icon];
  return (
    <button type="button" className="icon-btn" aria-label={label} title={label} {...rest}>
      <C />
    </button>
  );
}

// ---------- form bits ----------

export function Field({ label, hint, children, className = '' }: { label: string; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={`field ${className}`}>
      <span>
        {label} {hint ? <em className="hint" style={{ fontStyle: 'normal' }}>{hint}</em> : null}
      </span>
      {children}
    </label>
  );
}

/**
 * For a sheet a barcode scanner types into (Add a phone or device, Add a product): the scanner
 * presses Enter after every code, so Enter goes to the next box there instead of pressing the
 * sheet's main button, and nothing is added until someone taps it. Goes on the box around the
 * boxes, with data-enter="off".
 */
export function enterToNextBox(e: ReactKeyboardEvent<HTMLElement>): void {
  if (e.key !== 'Enter' || e.shiftKey || e.altKey || e.ctrlKey || e.metaKey || e.nativeEvent.isComposing) return;
  const t = e.target as HTMLInputElement;
  if (t.tagName !== 'INPUT' || ['checkbox', 'radio', 'button', 'submit', 'file'].includes(t.type)) return;
  e.preventDefault();
  const boxes = Array.from(e.currentTarget.querySelectorAll<HTMLInputElement>('input, select, textarea')).filter((b) => !b.disabled && b.type !== 'hidden' && b.offsetParent !== null);
  boxes[boxes.indexOf(t) + 1]?.focus();
}

export function MoneyInput({
  value,
  onChange,
  autoFocus,
  placeholder = '0.00',
  className = '',
  id,
  ariaLabel,
  disabled,
}: {
  value: number | null;
  onChange: (cents: number | null) => void;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  ariaLabel?: string;
  /** Shown but locked (a paid repair's price). */
  disabled?: boolean;
}) {
  const [text, setText] = useState(value === null ? '' : (value / 100).toFixed(2));
  const last = useRef(value);
  useEffect(() => {
    if (value !== last.current) {
      setText(value === null ? '' : (value / 100).toFixed(2));
      last.current = value;
    }
  }, [value]);
  return (
    <div style={{ position: 'relative' }}>
      <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--slate)', fontWeight: 700 }}>$</span>
      <input
        id={id}
        aria-label={ariaLabel}
        className={`input input-money ${className}`}
        style={{ paddingLeft: 30 }}
        inputMode="decimal"
        autoComplete="off"
        autoFocus={autoFocus}
        placeholder={placeholder}
        disabled={disabled}
        value={text}
        onChange={(e) => {
          const t = e.target.value.replace(/[^\d.]/g, '');
          setText(t);
          const c = t === '' ? null : parseMoney(t);
          last.current = c;
          onChange(c);
        }}
        onBlur={() => {
          if (last.current !== null) setText((last.current / 100).toFixed(2));
        }}
      />
    </div>
  );
}

export const Money = ({ cents, className = '' }: { cents: number | null | undefined; className?: string }) => (
  <span className={`num ${className}`}>{cents === null || cents === undefined ? '—' : formatCents(cents)}</span>
);

/**
 * Money in a bubble, the same everywhere in the POS: what something cost in red, what it made in
 * green (a loss turns red and says so). Nothing at all when there's no amount, which is what the
 * server sends anyone who doesn't get to see costs. `label` is the word in the bubble ("Cost",
 * "Profit", or your own); false leaves the word out, for a column that already has a heading.
 */
export function MoneyPill({ kind, cents, label, size, className = '' }: { kind: 'cost' | 'profit'; cents: number | null | undefined; label?: string | false; size?: 'lg'; className?: string }) {
  if (cents === null || cents === undefined) return null;
  const loss = kind === 'profit' && cents < 0;
  const tone = kind === 'cost' || loss ? 'cost' : 'profit';
  const word = label === false ? null : label ?? (loss ? 'Loss' : kind === 'cost' ? 'Cost' : 'Profit');
  return (
    <span className={`pill pill-${tone}${size === 'lg' ? ' pill-lg' : ''} ${className}`.trim()} title={word ? undefined : kind === 'cost' ? 'Cost' : loss ? 'Loss' : 'Profit'}>
      {word ? <span className="pill-k">{word}</span> : null}
      <span className="num">{formatCents(loss ? -cents : cents)}</span>
    </span>
  );
}
/** A cost, in a red bubble. */
export const Cost = (p: Omit<Parameters<typeof MoneyPill>[0], 'kind'>) => <MoneyPill kind="cost" {...p} />;
/** A profit, in a green bubble (a loss in red). */
export const Profit = (p: Omit<Parameters<typeof MoneyPill>[0], 'kind'>) => <MoneyPill kind="profit" {...p} />;
/** The class for a stat tile that holds a cost (red) or a profit (green; a loss red). */
export const statTone = (kind: 'cost' | 'profit', cents: number | null | undefined): string => (cents === null || cents === undefined ? '' : kind === 'cost' || cents < 0 ? 'stat-cost' : 'stat-profit');

export function Badge({ tone = 'plain', icon, children }: { tone?: 'plain' | 'blue' | 'green' | 'amber' | 'red'; icon?: IconName; children: ReactNode }) {
  const C = icon ? Icon[icon] : null;
  return (
    <span className={`badge ${tone !== 'plain' ? `badge-${tone}` : ''}`}>
      {C ? <C /> : null}
      {children}
    </span>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return <div className="spinner" role="status" aria-label={label} />;
}

export function Empty({ icon = 'info', title, children }: { icon?: IconName; title: string; children?: ReactNode }) {
  const C = Icon[icon];
  return (
    <div className="empty">
      <C />
      <div className="strong" style={{ color: 'var(--navy)' }}>{title}</div>
      {children ? <div>{children}</div> : null}
    </div>
  );
}

export function Tabs<T extends string>({ tabs, value, onChange, label }: { tabs: { id: T; label: string }[]; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {tabs.map((t) => (
        <button key={t.id} type="button" role="tab" className="tab" aria-selected={value === t.id} onClick={() => onChange(t.id)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ---------- sheets (dialogs) ----------

// The sheets open right now, the newest last: Escape closes only the one on top (a manager's PIN
// over Payment, Remove taxes over the cart), not everything under it too.
const openSheets: object[] = [];

export function Sheet({ title, onClose, children, footer, wide, labelledBy, focusFirst = true, dismissOnBackdrop = true }: {
  title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean; labelledBy?: string;
  /** False: the sheet itself takes the focus, not its first box (no keyboard popping up; a scan isn't typed into a box). */
  focusFirst?: boolean;
  /**
   * False: a tap outside the sheet doesn't close it (it holds something a stray tap would lose,
   * like payments already taken). The close button and Escape still call onClose.
   */
  dismissOnBackdrop?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const me = {};
    openSheets.push(me);
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const first = focusFirst ? el?.querySelector<HTMLElement>('[autofocus], input, select, textarea, button:not([data-close])') : null;
    (first ?? el)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openSheets[openSheets.length - 1] === me) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const i = openSheets.indexOf(me);
      if (i >= 0) openSheets.splice(i, 1);
      prev?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const id = labelledBy ?? `sheet-${title.replace(/\W+/g, '-').toLowerCase()}`;
  // Enter in a box presses the sheet's main button (Open drawer, Save, Add…), like a form.
  // Not in a text area (new line), a search box or a box that handles Enter itself.
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' || e.defaultPrevented || e.shiftKey || e.altKey || e.ctrlKey || e.metaKey || e.nativeEvent.isComposing) return;
    const t = e.target as HTMLElement;
    if (t.tagName !== 'INPUT' || t.closest('form, .search, [data-enter="off"]')) return;
    if (['checkbox', 'radio', 'button', 'submit', 'file', 'search'].includes((t as HTMLInputElement).type)) return;
    const main = ref.current?.querySelector<HTMLButtonElement>('.sheet-foot .btn-primary:not([disabled])');
    if (!main) return;
    e.preventDefault();
    main.click();
  };
  return (
    <div className="overlay" onMouseDown={(e) => { if (dismissOnBackdrop && e.target === e.currentTarget) onClose(); }}>
      <div className={`sheet ${wide ? 'sheet-wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={id} ref={ref} tabIndex={-1} onKeyDown={onKeyDown}>
        <div className="sheet-head">
          <h2 id={id}>{title}</h2>
          <IconButton icon="close" label="Close" data-close onClick={onClose} />
        </div>
        <div className="sheet-body">{children}</div>
        {footer ? <div className="sheet-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

// ---------- toasts ----------

interface Toast { id: number; text: string; kind: 'info' | 'error' | 'good' }
const ToastCtx = createContext<(text: string, kind?: Toast['kind']) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((text: string, kind: Toast['kind'] = 'info') => {
    const id = Date.now() + Math.random();
    // At most two at a time, and quick: the screen itself shows what changed.
    setItems((xs) => [...xs.filter((x) => x.text !== text).slice(-1), { id, text, kind }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), kind === 'error' ? 5000 : kind === 'info' ? 3200 : 2200);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            {t.kind === 'error' ? <Icon.alert /> : t.kind === 'good' ? <Icon.check /> : <Icon.info />}
            <span>{t.text}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  return useContext(ToastCtx);
}

// ---------- PIN pad ----------

export function PinPad({ onSubmit, busy, error, autoFocus = true }: { onSubmit: (pin: string) => void; busy?: boolean; error?: string | null; autoFocus?: boolean }) {
  const [pin, setPin] = useState('');
  const submitted = useRef(false);
  useEffect(() => {
    if (error) setPin('');
    submitted.current = false;
  }, [error]);
  const press = (d: string) => {
    if (busy) return;
    setPin((p) => {
      const next = (p + d).slice(0, 6);
      if (next.length === 6 && !submitted.current) {
        submitted.current = true;
        setTimeout(() => onSubmit(next), 0);
      }
      return next;
    });
  };
  const back = () => setPin((p) => p.slice(0, -1));
  const enter = () => {
    if (pin.length >= 4 && !busy) {
      submitted.current = true;
      onSubmit(pin);
    }
  };
  useEffect(() => {
    if (!autoFocus) return;
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return;
      if (/^\d$/.test(e.key)) press(e.key);
      else if (e.key === 'Backspace') back();
      else if (e.key === 'Enter') enter();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  return (
    <div className="stack">
      <div className="pin-dots" aria-label={`${pin.length} digits entered`}>
        {Array.from({ length: Math.max(4, pin.length) }).map((_, i) => (
          <i key={i} className={i < pin.length ? 'on' : ''} />
        ))}
      </div>
      {error ? <div className="error-text" style={{ textAlign: 'center' }} role="alert">{error}</div> : null}
      <div className="pinpad">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button key={d} type="button" onClick={() => press(d)} aria-label={d}>{d}</button>
        ))}
        <button type="button" onClick={back} aria-label="Delete"><Icon.backspace /></button>
        <button type="button" onClick={() => press('0')} aria-label="0">0</button>
        <button type="button" onClick={enter} aria-label="Enter" style={{ background: pin.length >= 4 ? 'var(--blue)' : undefined, color: pin.length >= 4 ? 'white' : undefined }} disabled={busy}>
          <Icon.check />
        </button>
      </div>
    </div>
  );
}

// ---------- manager approval ----------

export interface ApprovalInput { employeeId: string; pin: string }
/** `roles`: whose role can approve it (the server says; the owner alone for cancelling a repair). */
type Pending = { label: string; roles: string[] | null; resolve: (a: ApprovalInput | null) => void };
const ApprovalCtx = createContext<(label: string, roles?: string[] | null) => Promise<ApprovalInput | null>>(async () => null);
/** Only the owner can approve it. */
const ownerOnly = (roles: string[] | null | undefined) => !!roles?.length && roles.every((r) => r === 'owner');

/** Asks a manager (or the owner, for what only the owner can do) to approve with their PIN when the server says approval is needed. */
export function ApprovalProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [managers, setManagers] = useState<{ id: string; name: string; role: string; initials: string }[]>([]);
  const [who, setWho] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ask = useCallback((label: string, roles?: string[] | null) => new Promise<ApprovalInput | null>((resolve) => {
    setError(null);
    setWho(null);
    setPending({ label, roles: roles ?? null, resolve });
    get('/api/status')
      .then((st) => setManagers((st.device?.employees ?? []).filter((e: any) => ['Manager', 'District Manager', 'Owner'].includes(e.role) && (!roles?.length || roles.includes(e.role_id)))))
      .catch(() => setManagers([]));
  }), []);
  const close = (a: ApprovalInput | null) => {
    pending?.resolve(a);
    setPending(null);
  };
  const owner = ownerOnly(pending?.roles);
  return (
    <ApprovalCtx.Provider value={ask}>
      {children}
      {pending ? (
        <Sheet title={owner ? 'Owner approval' : 'Manager approval'} onClose={() => close(null)}>
          <div className="banner"><Icon.lock /><div><div className="strong">{owner ? 'The owner needs to approve this.' : 'A manager needs to approve this.'}</div><div className="small">{pending.label}</div></div></div>
          {!who ? (
            managers.length ? (
              <div className="people">
                {managers.map((m) => (
                  <button key={m.id} type="button" className="person" onClick={() => setWho(m.id)}>
                    <span className="avatar">{m.initials}</span>
                    <span className="nm">{m.name}</span>
                    <span className="rl">{m.role}</span>
                  </button>
                ))}
              </div>
            ) : (
              <Empty icon="users" title={owner ? 'The owner can’t sign in on this tablet.' : 'No managers can sign in on this tablet.'} />
            )
          ) : (
            <div className="stack">
              <div className="row"><Button size="sm" variant="ghost" icon="chevronLeft" onClick={() => setWho(null)}>Pick someone else</Button></div>
              <PinPad error={error} onSubmit={(pin) => close({ employeeId: who, pin })} />
            </div>
          )}
        </Sheet>
      ) : null}
    </ApprovalCtx.Provider>
  );
}

export function useApproval() {
  return useContext(ApprovalCtx);
}

/**
 * Runs an action; if the server answers "approval_required", asks for a manager
 * PIN and runs it again with the approval. Errors become toasts.
 */
export function useAction() {
  const ask = useApproval();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const run = useCallback(
    async <T,>(fn: (approval?: ApprovalInput) => Promise<T>, opts: { success?: string; quiet?: boolean } = {}): Promise<T | undefined> => {
      setBusy(true);
      try {
        let approval: ApprovalInput | undefined;
        // Whose PIN can approve it (the server says): kept for a retry after a wrong PIN.
        let roles: string[] | null = null;
        for (let attempt = 0; attempt < 4; attempt++) {
          try {
            const r = await fn(approval);
            if (opts.success) toast(opts.success, 'good');
            return r;
          } catch (e) {
            if (e instanceof ApiError && (e.code === 'approval_required' || ((e.code === 'wrong_pin' || e.code === 'approval_denied' || e.code === 'locked') && approval))) {
              if (approval && e.code !== 'approval_required') toast(e.message, 'error');
              if (e.code === 'approval_required') roles = Array.isArray(e.details?.roles) ? e.details.roles : null;
              const a = await ask(e.code === 'approval_required' ? e.message : ownerOnly(roles) ? 'Try again with the owner’s PIN.' : 'Try again with a manager PIN.', roles);
              if (!a) return undefined;
              approval = a;
              continue;
            }
            throw e;
          }
        }
        return undefined;
      } catch (e) {
        if (!opts.quiet) toast(e instanceof Error ? e.message : 'Something went wrong.', 'error');
        if (opts.quiet) throw e;
        return undefined;
      } finally {
        setBusy(false);
      }
    },
    [ask, toast],
  );
  return { run, busy };
}

export function Confirm({ title, children, confirmLabel, danger, onConfirm, onClose, busy }: { title: string; children: ReactNode; confirmLabel: string; danger?: boolean; onConfirm: () => void; onClose: () => void; busy?: boolean }) {
  return (
    <Sheet
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} busy={busy}>{confirmLabel}</Button>
        </>
      }
    >
      {children}
    </Sheet>
  );
}
